import type { Editor } from "@tiptap/core";
import { useEditorState } from "@tiptap/react";
import clsx from "clsx";
import { Bold, Code, Italic, Link2, Plus, Redo2, Undo2 } from "lucide-react";
import type { ReactNode } from "react";
import { Menu } from "../../components/ui/Menu";
import { editLink } from "./commands";
import { SLASH_ITEMS } from "./slash-items";

function ToolButton(props: {
  label: string;
  shortcut?: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={props.label}
      aria-pressed={props.pressed}
      title={props.shortcut ? `${props.label} (${props.shortcut})` : props.label}
      disabled={props.disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={props.onClick}
      className={clsx(
        "grid h-7 w-7 place-items-center rounded-md text-ink-2 hover:bg-green-wash hover:text-green-ink disabled:opacity-40",
        props.pressed && "bg-green-wash text-green-ink",
      )}
    >
      {props.children}
    </button>
  );
}

export function FormatBar({ editor }: { editor: Editor }) {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      code: e.isActive("code"),
      link: e.isActive("link"),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
      editable: e.isEditable,
      insertIds: SLASH_ITEMS.filter((i) => i.available(e))
        .map((i) => i.id)
        .join(","),
    }),
  });
  const insertItems = SLASH_ITEMS.filter((i) => s.insertIds.split(",").includes(i.id)).map((i) => ({
    id: i.id,
    label: i.label,
    hint: i.hint,
    onSelect: () => {
      if (editor.isEditable) Promise.resolve(i.run(editor)).catch(() => {});
    },
  }));
  const run = (fn: () => void) => () => fn();

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="flex items-center gap-0.5 rounded-lg border border-line bg-surface/95 px-1 py-0.5 shadow-sm backdrop-blur"
    >
      <ToolButton
        label="Bold"
        shortcut="⌘B"
        pressed={s.bold}
        disabled={!s.editable}
        onClick={run(() => editor.chain().focus().toggleMark("bold").run())}
      >
        <Bold size={15} />
      </ToolButton>
      <ToolButton
        label="Italic"
        shortcut="⌘I"
        pressed={s.italic}
        disabled={!s.editable}
        onClick={run(() => editor.chain().focus().toggleMark("italic").run())}
      >
        <Italic size={15} />
      </ToolButton>
      <ToolButton
        label="Inline code"
        shortcut="⌘E"
        pressed={s.code}
        disabled={!s.editable}
        onClick={run(() => editor.chain().focus().toggleMark("code").run())}
      >
        <Code size={15} />
      </ToolButton>
      <ToolButton
        label="Link"
        shortcut="⌘K"
        pressed={s.link}
        disabled={!s.editable}
        onClick={run(() => void editLink(editor))}
      >
        <Link2 size={15} />
      </ToolButton>
      <span className="mx-1 h-4 w-px bg-line" aria-hidden="true" />
      <Menu
        label="Insert block"
        items={insertItems}
        trigger={
          <button
            type="button"
            disabled={!s.editable}
            className="flex h-7 items-center gap-1 rounded-md px-2 text-sm text-ink-2 hover:bg-green-wash hover:text-green-ink"
          >
            <Plus size={14} /> Insert
          </button>
        }
      />
      <span className="mx-1 h-4 w-px bg-line" aria-hidden="true" />
      <ToolButton
        label="Undo"
        shortcut="⌘Z"
        disabled={!s.editable || !s.canUndo}
        onClick={run(() => editor.chain().focus().undo().run())}
      >
        <Undo2 size={15} />
      </ToolButton>
      <ToolButton
        label="Redo"
        shortcut="⇧⌘Z"
        disabled={!s.editable || !s.canRedo}
        onClick={run(() => editor.chain().focus().redo().run())}
      >
        <Redo2 size={15} />
      </ToolButton>
    </div>
  );
}
